/**
 * @alfin-crm/contracts — Domain Error Codes
 *
 * Typed error codes used in API responses and thrown by services.
 */
import { z } from 'zod';

export const ErrorCode = {
  // Authentication
  UNAUTHORIZED: 'UNAUTHORIZED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  REFRESH_TOKEN_INVALID: 'REFRESH_TOKEN_INVALID',
  TWO_FACTOR_REQUIRED: 'TWO_FACTOR_REQUIRED',
  TWO_FACTOR_INVALID: 'TWO_FACTOR_INVALID',
  INVITATION_INVALID: 'INVITATION_INVALID',
  PASSWORD_POLICY_VIOLATION: 'PASSWORD_POLICY_VIOLATION',

  // Authorization
  FORBIDDEN: 'FORBIDDEN',
  TENANT_MISMATCH: 'TENANT_MISMATCH',
  TEAM_SCOPE_VIOLATION: 'TEAM_SCOPE_VIOLATION',

  // Validation
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  DUPLICATE_ENTRY: 'DUPLICATE_ENTRY',
  INVALID_STATUS_TRANSITION: 'INVALID_STATUS_TRANSITION',
  PHONE_FORMAT_INVALID: 'PHONE_FORMAT_INVALID',

  // Resources
  NOT_FOUND: 'NOT_FOUND',
  COMPANY_NOT_FOUND: 'COMPANY_NOT_FOUND',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  CONTACT_NOT_FOUND: 'CONTACT_NOT_FOUND',
  TRANSACTION_NOT_FOUND: 'TRANSACTION_NOT_FOUND',
  CAMPAIGN_NOT_FOUND: 'CAMPAIGN_NOT_FOUND',
  TEMPLATE_NOT_FOUND: 'TEMPLATE_NOT_FOUND',
  CONVERSATION_NOT_FOUND: 'CONVERSATION_NOT_FOUND',

  // Business rules
  APPROVAL_REQUIRED: 'APPROVAL_REQUIRED',
  CAMPAIGN_CANNOT_BE_MODIFIED: 'CAMPAIGN_CANNOT_BE_MODIFIED',
  TEMPLATE_NOT_APPROVED: 'TEMPLATE_NOT_APPROVED',
  IMPORT_IN_PROGRESS: 'IMPORT_IN_PROGRESS',

  // AI
  AI_SCOPE_VIOLATION: 'AI_SCOPE_VIOLATION',
  AI_SQL_INVALID: 'AI_SQL_INVALID',
  AI_QUOTA_EXCEEDED: 'AI_QUOTA_EXCEEDED',
  AI_SERVICE_UNAVAILABLE: 'AI_SERVICE_UNAVAILABLE',

  // Infrastructure
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  RATE_LIMITED: 'RATE_LIMITED',
  UPLOAD_TOO_LARGE: 'UPLOAD_TOO_LARGE',
  UPLOAD_TYPE_INVALID: 'UPLOAD_TYPE_INVALID',
  WEBHOOK_SIGNATURE_INVALID: 'WEBHOOK_SIGNATURE_INVALID',
} as const;

export type ErrorCodeKey = (typeof ErrorCode)[keyof typeof ErrorCode];

/** Standard API error response body */
export const ApiErrorSchema = z.object({
  success: z.literal(false),
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.unknown()).optional(),
    requestId: z.string().optional(),
  }),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;

/** Standard API success response body */
export const ApiSuccessSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.literal(true),
    data: dataSchema,
    meta: z
      .object({
        requestId: z.string().optional(),
        page: z.number().int().optional(),
        limit: z.number().int().optional(),
        total: z.number().int().optional(),
        hasNext: z.boolean().optional(),
        nextCursor: z.string().optional(),
      })
      .optional(),
  });
